import { cellKey, type WeeklyWorkload } from './saveConflicts';

/**
 * The draft of the Saisie (#55): the changes waiting to be saved are kept in the browser, per
 * account, so that changing page (or reloading) does not lose them. This module is the logic, as
 * pure functions: what is written, how it is read back (and when it is ignored), how it is
 * reconciled with the grid that has just been loaded, and what the message of the resumption says.
 * The persistence over time (debounce, unmount) is `hooks/useSaisieDrafts.ts`.
 *
 * What is kept is only ids and numbers, no name of anyone: the draft says « this cell, this
 * value, over this saved value ». That last one, `base_days`, is the saved value of the cell as the
 * user saw it when they changed it. It is what the save will expect the week to still hold, so that a
 * cell a colleague saved in the meantime comes back as a conflict, never overwritten in silence.
 */

/** The shape of what is stored. Raise it when the shape changes: an older draft is then ignored. */
export const DRAFT_VERSION = 1;
/** A draft older than this (since it was last written) is ignored and erased. */
export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** Left of the account id in the storage key. */
export const DRAFT_KEY_PREFIX = 'saisie-drafts:';

export const draftKey = (userId: string): string => `${DRAFT_KEY_PREFIX}${userId}`;

type CellRef = Pick<WeeklyWorkload, 'consultant_id' | 'project_id' | 'week_start'>;

export interface DraftChange extends CellRef {
  days: number;
  /** The saved value the user saw when they changed the cell. */
  base_days: number;
  /** The cell was already waiting on a choice (a colleague changed it) when the draft was written. */
  conflict?: { current_days: number };
}

export interface SaisieDraft {
  version: number;
  /** The account it belongs to. */
  user_id: string;
  /** When it was last written, ISO. */
  saved_at: string;
  changes: DraftChange[];
}

// A change within this is the same value (the server rounds days to 2 decimals)
const SAME_DAYS = 0.005;
export const sameDays = (a: number, b: number): boolean => Math.abs(a - b) < SAME_DAYS;

// ------------------------------------------------------------------------ writing

/**
 * The draft of the pending changes. A change that carries no base (not typed in the grid) is
 * written with a base of 0: should the cell hold something when the draft is taken up again, it
 * comes back as a conflict, which is the safe side.
 */
export const buildDraft = (userId: string, pending: readonly WeeklyWorkload[], now: Date): SaisieDraft => ({
  version: DRAFT_VERSION,
  user_id: userId,
  saved_at: now.toISOString(),
  changes: pending.map((change) => ({
    consultant_id: change.consultant_id,
    project_id: change.project_id,
    week_start: change.week_start,
    days: change.days,
    base_days: change.base_days ?? 0,
    ...(change.conflict ? { conflict: { current_days: change.conflict.current_days } } : {}),
  })),
});

export const serializeDraft = (draft: SaisieDraft): string => JSON.stringify(draft);

// ------------------------------------------------------------------------ reading

export type ParsedDraft =
  | { kind: 'none' }
  | { kind: 'ok'; draft: SaisieDraft }
  /** `erase`: what is stored is of no use to anyone and goes. Another account's draft is left alone. */
  | { kind: 'ignored'; reason: 'expired' | 'invalid' | 'version' | 'other-account'; erase: boolean };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const MAX_CHANGES = 2000;

const isId = (value: unknown): value is string => typeof value === 'string' && value.length > 0 && value.length <= 100;
const isNumber = (value: unknown, max: number): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= max;

/** A change as stored, or null when it is not one (the rest of the draft is kept). */
const readChange = (raw: unknown): DraftChange | null => {
  if (typeof raw !== 'object' || raw === null) return null;
  const item = raw as Record<string, unknown>;
  if (!isId(item.consultant_id) || !isId(item.project_id)) return null;
  if (typeof item.week_start !== 'string' || !ISO_DATE.test(item.week_start)) return null;
  if (!isNumber(item.days, 5) || !isNumber(item.base_days, 100)) return null;
  const change: DraftChange = {
    consultant_id: item.consultant_id,
    project_id: item.project_id,
    week_start: item.week_start,
    days: item.days,
    base_days: item.base_days,
  };
  const conflict = item.conflict as { current_days?: unknown } | undefined;
  if (conflict && isNumber(conflict.current_days, 100)) change.conflict = { current_days: conflict.current_days };
  return change;
};

/**
 * What the storage held under the account's key. Ignored when it is not JSON, is of another
 * version, belongs to another account, or was last written more than 7 days ago; one change that
 * is not valid is dropped and the others kept; a draft with nothing left is nothing.
 */
export const parseDraft = (raw: string | null, userId: string, now: Date): ParsedDraft => {
  if (raw === null) return { kind: 'none' };
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return { kind: 'ignored', reason: 'invalid', erase: true };
  }
  if (typeof data !== 'object' || data === null) return { kind: 'ignored', reason: 'invalid', erase: true };
  const stored = data as Record<string, unknown>;
  if (stored.version !== DRAFT_VERSION) return { kind: 'ignored', reason: 'version', erase: true };
  if (stored.user_id !== userId) return { kind: 'ignored', reason: 'other-account', erase: false };
  const savedAt = typeof stored.saved_at === 'string' ? Date.parse(stored.saved_at) : NaN;
  if (!Number.isFinite(savedAt) || !Array.isArray(stored.changes)) return { kind: 'ignored', reason: 'invalid', erase: true };
  if (now.getTime() - savedAt > DRAFT_MAX_AGE_MS) return { kind: 'ignored', reason: 'expired', erase: true };

  // One entry per cell, the last one winning
  const byCell = new Map<string, DraftChange>();
  for (const raw of stored.changes.slice(0, MAX_CHANGES)) {
    const change = readChange(raw);
    if (change) byCell.set(cellKey(change), change);
  }
  if (byCell.size === 0) return { kind: 'none' };
  return {
    kind: 'ok',
    draft: { version: DRAFT_VERSION, user_id: userId, saved_at: new Date(savedAt).toISOString(), changes: [...byCell.values()] },
  };
};

// ------------------------------------------------------------------------ storage

/** The part of `Storage` the draft uses: a test gives its own. */
export type DraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** The browser's `localStorage`, or null where it is not there at all (a private mode, a policy). */
const browserStorage = (): DraftStorage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
};

/**
 * Reads the account's draft. Every access to the storage is guarded: the page works without it
 * (nothing is kept, nothing is restored). What is not usable (too old, unreadable, of another
 * version) is erased.
 */
export const readDraft = (userId: string, now: Date, storage: DraftStorage | null = browserStorage()): ParsedDraft => {
  if (!storage) return { kind: 'none' };
  let raw: string | null;
  try {
    raw = storage.getItem(draftKey(userId));
  } catch {
    return { kind: 'none' };
  }
  const parsed = parseDraft(raw, userId, now);
  if (parsed.kind === 'none' && raw !== null) clearDraft(userId, storage);
  if (parsed.kind === 'ignored' && parsed.erase) clearDraft(userId, storage);
  return parsed;
};

/**
 * Writes the pending changes as the account's draft, or erases it when nothing is pending. False
 * when the storage refused (full, blocked): the draft is then simply not kept.
 */
export const writeDraft = (
  userId: string,
  pending: readonly WeeklyWorkload[],
  now: Date,
  storage: DraftStorage | null = browserStorage(),
): boolean => {
  if (!storage) return false;
  try {
    if (pending.length === 0) storage.removeItem(draftKey(userId));
    else storage.setItem(draftKey(userId), serializeDraft(buildDraft(userId, pending, now)));
    return true;
  } catch {
    return false;
  }
};

/** Erases the account's draft (all saved, abandoned, or signed out). */
export const clearDraft = (userId: string | null | undefined, storage: DraftStorage | null = browserStorage()): void => {
  if (!storage || !userId) return;
  try {
    storage.removeItem(draftKey(userId));
  } catch {
    /* the page works without storage */
  }
};

// -------------------------------------------------------------------- reconciliation

/** What the page knows of the grid it has just loaded, to check a draft against. */
export interface DraftGrid {
  /**
   * The value the cell holds on the server, as the grid just loaded shows it (0 for a project
   * the consultant has no week on). Null when the grid does not hold the cell at all: the
   * consultant is not in what was loaded, or the week is outside the displayed range.
   */
  savedDays(cell: CellRef): number | null;
  /** The grid has a row for this consultant and this project. */
  hasRow(cell: CellRef): boolean;
  /** The cell is on screen: its consultant passes the filters, and its week is in the range. */
  isShown(cell: CellRef): boolean;
}

export interface DraftResumption {
  /** What goes back to the pending list. */
  pending: WeeklyWorkload[];
  /** Project rows the grid can show but does not have (a project added to the consultant and never saved). */
  missingRows: Array<Pick<WeeklyWorkload, 'consultant_id' | 'project_id'>>;
  counts: {
    /** The changes taken up again. */
    resumed: number;
    /** Among them, those waiting on a choice (someone else saved the cell in the meantime). */
    conflicts: number;
    /** Among them, those the page does not show (consultant outside the filters, week outside the range). */
    hidden: number;
    /** Drafts that disappeared because the cell already holds the drafted value. */
    dropped: number;
  };
}

/**
 * Checks each drafted change against the grid that has just been loaded, so that no one is
 * overwritten:
 * - the cell already holds the drafted value: the change disappears;
 * - it holds what the draft saw (`base_days`): the change is waiting again, as it was;
 * - it holds something else: someone saved it in the meantime, the change is in conflict
 *   (`conflict.current_days` is that value) and the user picks a side;
 * - a change that was already in conflict stays so, with the value it now holds;
 * - the grid does not hold the cell (a consultant outside what was loaded, a week outside the
 *   range): the change is waiting as it was. Its `base_days` stays what the save expects, and the
 *   server refuses it if the week changed since: the conflict then shows.
 */
export const reconcileDraft = (changes: readonly DraftChange[], grid: DraftGrid): DraftResumption => {
  const pending: WeeklyWorkload[] = [];
  const missingRows = new Map<string, Pick<WeeklyWorkload, 'consultant_id' | 'project_id'>>();
  let dropped = 0;
  let conflicts = 0;
  let hidden = 0;

  for (const change of changes) {
    const current = grid.savedDays(change);
    const base: WeeklyWorkload = {
      consultant_id: change.consultant_id,
      project_id: change.project_id,
      week_start: change.week_start,
      days: change.days,
      base_days: change.base_days,
    };

    let resumed: WeeklyWorkload;
    if (current === null) {
      resumed = change.conflict ? { ...base, conflict: { current_days: change.conflict.current_days } } : base;
    } else if (sameDays(current, change.days)) {
      dropped += 1;
      continue;
    } else if (change.conflict || !sameDays(current, change.base_days)) {
      resumed = { ...base, conflict: { current_days: current } };
    } else {
      resumed = base;
    }

    if (current !== null && !grid.hasRow(change)) {
      missingRows.set(`${change.consultant_id}-${change.project_id}`, { consultant_id: change.consultant_id, project_id: change.project_id });
    }
    if (resumed.conflict) conflicts += 1;
    if (!grid.isShown(change)) hidden += 1;
    pending.push(resumed);
  }

  return { pending, missingRows: [...missingRows.values()], counts: { resumed: pending.length, conflicts, hidden, dropped } };
};

// ----------------------------------------------------------------------- the message

const plural = (count: number, one: string, many: string): string => (count > 1 ? many : one);

export interface ResumeMessage {
  /** « 3 modifications non enregistrées reprises ». */
  headline: string;
  /** The « dont … » lines, only those that apply. */
  details: string[];
}

/**
 * The message of the resumption. `conflicts` and `hidden` are among `resumed`; a line with a zero
 * is not written.
 */
export const describeResume = ({ resumed, conflicts, hidden }: { resumed: number; conflicts: number; hidden: number }): ResumeMessage => {
  const details: string[] = [];
  if (conflicts > 0) {
    details.push(
      `dont ${conflicts} ${plural(conflicts, 'modifiée', 'modifiées')} entre-temps par quelqu'un : choisissez la valeur à garder (${plural(conflicts, 'case encadrée', 'cases encadrées')})`,
    );
  }
  if (hidden > 0) details.push(`dont ${hidden} hors des filtres affichés`);
  return {
    headline: `${resumed} ${plural(resumed, 'modification non enregistrée reprise', 'modifications non enregistrées reprises')}`,
    details,
  };
};
